import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, onSnapshot, getDoc, doc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { COLORS } from '../theme';

// HC-REPORT-001 (Apple 1.2 UGC moderation, Prompt #278): superadmin review
// queue for user-filed content reports. Modeled directly on
// RatingConsistencyReviewsPage.js's pattern (real-time onSnapshot on
// status == 'pending', deduplicated secondary getDoc fetches for display
// names, action button writes via a callable) — the closest existing
// precedent in this codebase for "flagged content, needs human review."
// Simpler than that page: a single "Mark Reviewed" action, no modal/reason
// input needed (reportContent's own `reason` field already captures why
// the report was filed — reviewContentReport just needs to know it was
// looked at, not why the reviewer agreed or disagreed).

const COL = '1.2fr 1.2fr 1fr 1.8fr 130px 130px';
const HEADERS = ['Reporter', 'Reported User', 'Reason', 'Message', 'Filed', 'Actions'];

const REASON_LABELS = {
  harassment: 'Harassment',
  spam: 'Spam',
  inappropriate_content: 'Inappropriate Content',
  other: 'Other',
};

function fmtDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function truncate(text, max) {
  if (!text) return '';
  return text.length > max ? text.slice(0, max) + '…' : text;
}

function Skeleton() {
  return (
    <div style={{ height: '13px', backgroundColor: COLORS.surfaceHover, borderRadius: '4px', width: '70%', animation: 'pulse 1.4s ease-in-out infinite' }} />
  );
}

export default function ContentReportsPage() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const [userNames, setUserNames] = useState({});
  const fetchedUserRef = useRef(new Set());

  const [reviewingId, setReviewingId] = useState(null);
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  }

  function nameFromUserDoc(id, data) {
    if (!data) return id.slice(0, 8);
    return data.displayName || [data.firstName, data.lastName].filter(Boolean).join(' ') || data.email || id.slice(0, 8);
  }

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'contentReports'), where('status', '==', 'pending')),
      snap => {
        const docs = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.createdAt?.toMillis?.() || 0) - (b.createdAt?.toMillis?.() || 0));
        setReports(docs);
        setLoading(false);

        // Secondary reads — reporter + reported-user display names (deduped,
        // both come from the same `users` collection so one uid set covers
        // both columns).
        const allUids = new Set();
        docs.forEach(d => {
          if (d.reporterUid) allUids.add(d.reporterUid);
          if (d.reportedUid) allUids.add(d.reportedUid);
        });
        const newUids = Array.from(allUids).filter(id => !fetchedUserRef.current.has(id));
        if (newUids.length > 0) {
          newUids.forEach(id => fetchedUserRef.current.add(id));
          Promise.all(
            newUids.map(id => getDoc(doc(db, 'users', id)).then(s => [id, nameFromUserDoc(id, s.exists() ? s.data() : null)]))
          ).then(entries => setUserNames(prev => ({ ...prev, ...Object.fromEntries(entries) })));
        }
      }
    );
    return () => unsub();
  }, []);

  async function handleMarkReviewed(reportId) {
    setReviewingId(reportId);
    setError('');
    try {
      await httpsCallable(functions, 'reviewContentReport')({ reportId });
      showToast('Report marked reviewed');
    } catch (err) {
      setError(err.message || 'Action failed');
    } finally {
      setReviewingId(null);
    }
  }

  const headerCell = { fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 0', fontWeight: '600' };
  const cell = { padding: '11px 0', fontSize: '12px', color: COLORS.textPrimary };
  const actionBtn = { background: 'none', border: 'none', color: COLORS.success, fontSize: '12px', cursor: 'pointer', padding: '0 8px 0 0', fontWeight: '500' };

  return (
    <div>
      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>

      {toast && (
        <div style={{
          position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
          backgroundColor: COLORS.success, color: 'white',
          padding: '10px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: '500',
          zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        }}>
          {toast}
        </div>
      )}

      {error && (
        <div style={{ fontSize: '12px', color: COLORS.danger, marginBottom: '12px' }}>{error}</div>
      )}

      <div style={{ marginBottom: '16px' }}>
        <span style={{ fontSize: '13px', color: COLORS.textSecondary }}>
          {loading ? '…' : reports.length === 0
            ? 'No pending content reports to review'
            : `${reports.length} report${reports.length !== 1 ? 's' : ''} pending review — oldest first`}
        </span>
      </div>

      <div style={{ backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: '8px', overflowX: 'auto' }}>
        <div style={{ minWidth: '900px' }}>

          <div style={{ display: 'grid', gridTemplateColumns: COL, padding: '0 16px', borderBottom: `1px solid ${COLORS.border}` }}>
            {HEADERS.map(h => <div key={h} style={headerCell}>{h}</div>)}
          </div>

          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: COL, padding: '14px 16px', borderBottom: `1px solid ${COLORS.border}30` }}>
                {Array.from({ length: 6 }).map((__, j) => <div key={j}><Skeleton /></div>)}
              </div>
            ))
          ) : reports.length === 0 ? (
            <div style={{ padding: '48px 16px', textAlign: 'center', color: COLORS.textMuted, fontSize: '13px' }}>
              No pending content reports to review.
            </div>
          ) : (
            reports.map((r, i) => (
              <div
                key={r.id}
                style={{
                  display: 'grid', gridTemplateColumns: COL, padding: '0 16px', alignItems: 'center',
                  borderBottom: i < reports.length - 1 ? `1px solid ${COLORS.border}30` : 'none',
                }}
              >
                <div style={{ ...cell, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {userNames[r.reporterUid] || '…'}
                </div>
                <div style={{ ...cell, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {userNames[r.reportedUid] || '…'}
                </div>
                <div style={cell}>{REASON_LABELS[r.reason] || r.reason}</div>
                <div style={{ ...cell, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', paddingRight: '8px' }}>
                  {r.messageTextSnapshot ? truncate(r.messageTextSnapshot, 60) : <span style={{ color: COLORS.textMuted }}>—</span>}
                </div>
                <div style={{ ...cell, color: COLORS.textSecondary, fontSize: '11px' }}>{fmtDate(r.createdAt)}</div>
                <div style={cell}>
                  <button
                    onClick={() => handleMarkReviewed(r.id)}
                    disabled={reviewingId === r.id}
                    style={{ ...actionBtn, opacity: reviewingId === r.id ? 0.6 : 1 }}
                  >
                    {reviewingId === r.id ? '…' : 'Mark Reviewed'}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
